import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { BreakpointsService } from '@sondermind/utilities/services';

export enum SonderModalSize {
  MEDIUM = 'medium',
  LARGE = 'large',
  FULLSCREEN = 'fullscreen'
}

const sizeClassList: SonderModalSize[] = [
  SonderModalSize.MEDIUM,
  SonderModalSize.LARGE,
  SonderModalSize.FULLSCREEN
];

/**
 * @deprecated For route-driven modals that host a `RouterOutlet` (e.g. deep-linked calendar flows), prefer
 * `iris-route-modal` from `@sondermind/iris/modal`. This component remains for overlay-style modals until migrated.
 */
@Component({
  selector: 'sonder-modal',
  templateUrl: './sonder-modal.component.html',
  styleUrls: ['./sonder-modal.component.scss'],
  host: {
    '[class.open]': 'open',
  },
  standalone: false
})

export class SonderModalComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  isMobile$ = this.bp.isMobile$;
  isMobile: boolean = false;
  mobileSubscription: Subscription;
  IrisIconButtonTypes = IrisIconButtonTypes;
  constructor(
    private bp: BreakpointsService,
    private focusTrapFactory: ConfigurableFocusTrapFactory
  ) {}

  @Input('sonderModalOpen') open: boolean;
  @Output('sonderModalOpenChange') openChanged$: EventEmitter<boolean | string> = new EventEmitter<boolean | string>(false);

  @Input('sonderModalCloseable') closeable: boolean = true;
  @Input('sonderModalHasBackdrop') hasBackdrop: boolean = true;

  // Prevent close on backdrop click
  @Input('sonderModalStaticBackdrop') staticBackdrop: boolean = false;

  @Input('sonderModalHasConfirmationModal') hasConfirmationModal: boolean = false;

  @Input('sonderModalSize') modalSize: SonderModalSize;
  modalSizeClass: string;

  @Output('sonderModalClosed') closed = this.openChanged$.pipe(
    tap(() => {
      if (this.closeable) {
        filter((x) => !x);
      }
    }),
  );

  @ViewChild('modalDivToFocusTrap') modalDivToFocusTrap: ElementRef<HTMLElement>;
  focusTrap: ConfigurableFocusTrap;

  ngOnInit(): void {
    this.mobileSubscription = this.isMobile$.subscribe(
      // eslint-disable-next-line no-return-assign
      (s) => this.isMobile = s
    );
    this.modalSizeClass = this.updateSonderModalSize();
  }

  ngAfterViewInit(): void {
    /* Create a focus trap for our modal's main div.
    Note: creating the focus trap via the ConfigurableFocusTrapFactory is preferable to using the cdkTrapFocus
    directive because ConfigurableFocusTraps are implicitly registered and managed by the FocusTrapManager,
    which properly handles toggling focus traps when multiple ones exist simultaneously (like in the case of stacked
    modals). The cdkTrapFocus directive on the other hand,
    simply uses FocusTrap and IS NOT managed by the FocusTrapManager. */
    if (this.modalDivToFocusTrap) {
      this.focusTrap = this.focusTrapFactory.create(this.modalDivToFocusTrap.nativeElement);
      this.focusTrap.enabled = this.open;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!changes['sonderModalSize']) {
      this.modalSizeClass = this.updateSonderModalSize();
    }

    if (changes['open'] && this.focusTrap) {
      this.focusTrap.enabled = this.open;
    }
  }

  ngOnDestroy(): void {
    this.mobileSubscription.unsubscribe();
    this.focusTrap?.destroy();
  }

  openSonderModal(): void {
    if (this.open) {
      return;
    }

    this.open = true;
    this.openChanged$.emit(this.open);
  }

  @HostListener('body:keyup.escape')
  closeSonderModal(xClicked: boolean = false): void {
    if (!this.closeable || !this.open) {
      return;
    }
    this.open = false;

    // Some modals have a dirty data prompt that opens if the user has edited the modal,
    // and they've clicked outside the modal. This should not open if the X icon is clicked,
    // so need to differentiate whether the modal was closed by clicking the X or the backdrop.
    if (this.hasConfirmationModal && xClicked) {
      this.openChanged$.emit('xClicked');
    } else {
      this.openChanged$.emit(this.open);
      if (!this.bp.hasMultipleModals) {
        this.bp.toggleModal(this.open);
      }
      this.bp.hasMultipleModals = false;
    }
  }

  private updateSonderModalSize(): SonderModalSize {
    const isValid = sizeClassList.includes(this.modalSize);
    return isValid ? this.modalSize : SonderModalSize.MEDIUM;
  }
}
